/*
MIT License

Copyright (c) 2020 Titus Wormer <tituswormer@gmail.com>

mdast-util-gfm-autolink-literal is from markdown only.
*/
import { gfmAutolinkLiteralFromMarkdown } from 'mdast-util-gfm-autolink-literal';
import type { InlineMath, Math } from '@blocksuite/markdown-math';
import { defaultHandlers } from 'mdast-util-to-markdown';
import {
  gfmFootnoteFromMarkdown,
  gfmFootnoteToMarkdown,
} from 'mdast-util-gfm-footnote';
import {
  gfmStrikethroughFromMarkdown,
  gfmStrikethroughToMarkdown,
} from 'mdast-util-gfm-strikethrough';
import { gfmTableFromMarkdown, gfmTableToMarkdown } from 'mdast-util-gfm-table';
import {
  gfmTaskListItemFromMarkdown,
  gfmTaskListItemToMarkdown,
} from 'mdast-util-gfm-task-list-item';
import { gfmAutolinkLiteral } from 'micromark-extension-gfm-autolink-literal';
import { gfmFootnote } from 'micromark-extension-gfm-footnote';
import { gfmStrikethrough } from 'micromark-extension-gfm-strikethrough';
import { gfmTable } from 'micromark-extension-gfm-table';
import { gfmTaskListItem } from 'micromark-extension-gfm-task-list-item';
import { combineExtensions } from 'micromark-util-combine-extensions';
import type { ListItem } from 'mdast';
import type { Options as ToMarkdownExtension } from 'mdast-util-to-markdown';
import type { Processor } from 'unified';

export function gfm() {
  return combineExtensions([
    gfmAutolinkLiteral(),
    gfmStrikethrough(),
    gfmTable(),
    gfmTaskListItem(),
    gfmFootnote(),
  ]);
}

function gfmFromMarkdown() {
  return [
    gfmStrikethroughFromMarkdown(),
    gfmTableFromMarkdown(),
    gfmTaskListItemFromMarkdown(),
    gfmAutolinkLiteralFromMarkdown(),
    gfmFootnoteFromMarkdown(),
  ];
}

function gfmToMarkdown() {
  return {
    extensions: [
      gfmStrikethroughToMarkdown(),
      gfmTableToMarkdown(),
      gfmTaskListItemToMarkdownWithDisplayMath(),
      gfmFootnoteToMarkdown(),
    ],
  };
}

/**
 * GFM's serializer only writes a task checkbox when the first list-item child
 * is a paragraph. BlockSuite represents a task's blank label as an empty
 * paragraph followed by its display-math child, so normalize that temporary
 * serialization shape to the unambiguous `- [x] $$...$$` form.
 */
function gfmTaskListItemToMarkdownWithDisplayMath(): ToMarkdownExtension {
  const taskList = gfmTaskListItemToMarkdown();

  return {
    ...taskList,
    handlers: {
      ...taskList.handlers,
      listItem(node, parent, state, info) {
        const item = node as ListItem;
        const normalized = normalizeTaskListDisplayMath(item);
        return (
          taskList.handlers?.listItem?.(
            normalized ?? node,
            parent,
            state,
            info
          ) ?? defaultHandlers.listItem(normalized ?? node, parent, state, info)
        );
      },
    },
  };
}

function normalizeTaskListDisplayMath(item: ListItem): ListItem | null {
  if (typeof item.checked !== 'boolean') return null;

  const first = item.children[0];
  const second = item.children[1];
  const hasEmptyLabel =
    first?.type === 'paragraph' && first.children.length === 0;
  const math = (
    first?.type === 'math'
      ? first
      : hasEmptyLabel && second?.type === 'math'
        ? second
        : undefined
  ) as Math | undefined;

  if (!math || math.meta || math.value.length === 0) return null;

  const inlineMath: InlineMath = {
    type: 'inlineMath',
    value: math.value,
    data: { _displayMath: true },
  } as InlineMath;
  const children = [
    {
      type: 'paragraph' as const,
      children: [inlineMath],
    },
    ...item.children.slice(hasEmptyLabel ? 2 : 1),
  ];

  return { ...item, children };
}

export function remarkGfm(this: Processor) {
  // oxlint-disable-next-line typescript/no-this-alias
  const self = this;
  const data = self.data();

  const micromarkExtensions =
    data.micromarkExtensions || (data.micromarkExtensions = []);
  const fromMarkdownExtensions =
    data.fromMarkdownExtensions || (data.fromMarkdownExtensions = []);
  const toMarkdownExtensions =
    data.toMarkdownExtensions || (data.toMarkdownExtensions = []);

  micromarkExtensions.push(gfm());
  fromMarkdownExtensions.push(gfmFromMarkdown());
  toMarkdownExtensions.push(gfmToMarkdown());
}
